import { createFileRoute } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import {
  COFFMAN_COVE,
  fetchMarineWeather,
  fetchMarineConditions,
  fetchTides,
  fetchTideHeights,
  type TidePrediction,
} from "@/lib/fishing-data";
import { TideDayChart } from "@/components/TideDayChart";
import { msUntilMinuteMark } from "@/lib/use-scheduled-reload";

import {
  Cloud,
  CloudRain,
  Wind,
  Eye,
  Thermometer,
  Droplets,
  Gauge,
  Sunrise,
  Sunset,
  Waves,
  Compass,
  Loader2,
  AlertTriangle,
} from "lucide-react";

export const Route = createFileRoute("/weather")({
  head: () =>
    seoHead({
      path: "/weather",
      title: "Coffman Cove Weather — Live Conditions & Forecast",
      description:
        "Live weather, wind, precipitation, and visibility for the Coffman Cove, Alaska fishing area, plus a 7-day at-a-glance forecast.",
    }),
  component: WeatherPage,
});

function degToCompass(deg: number) {
  const dirs = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  return dirs[Math.round(deg / 22.5) % 16];
}

const WEATHER_CODES: Record<number, { label: string; icon: typeof Cloud }> = {
  0: { label: "Clear sky", icon: Sunrise },
  1: { label: "Mainly clear", icon: Cloud },
  2: { label: "Partly cloudy", icon: Cloud },
  3: { label: "Overcast", icon: Cloud },
  45: { label: "Fog", icon: Cloud },
  48: { label: "Rime fog", icon: Cloud },
  51: { label: "Light drizzle", icon: CloudRain },
  53: { label: "Drizzle", icon: CloudRain },
  55: { label: "Dense drizzle", icon: CloudRain },
  61: { label: "Light rain", icon: CloudRain },
  63: { label: "Rain", icon: CloudRain },
  65: { label: "Heavy rain", icon: CloudRain },
  71: { label: "Light snow", icon: CloudRain },
  73: { label: "Snow", icon: CloudRain },
  75: { label: "Heavy snow", icon: CloudRain },
  80: { label: "Rain showers", icon: CloudRain },
  81: { label: "Rain showers", icon: CloudRain },
  82: { label: "Violent showers", icon: CloudRain },
  95: { label: "Thunderstorm", icon: CloudRain },
};

function codeInfo(code: number | undefined) {
  if (code === undefined) return { label: "—", icon: Cloud };
  return WEATHER_CODES[code] ?? { label: "Unknown", icon: Cloud };
}

function Widget({
  title,
  icon: Icon,
  stale = false,
  staleAgeLabel,
  children,
}: {
  title: string;
  icon: typeof Cloud;
  stale?: boolean;
  staleAgeLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-background/60 backdrop-blur p-5 shadow-sm relative">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
        <Icon size={14} />
        <span className="flex-1">{title}</span>
        {stale && (
          <span
            title={
              staleAgeLabel
                ? `Cached reading from ${staleAgeLabel} — upstream source is temporarily unreachable`
                : "Cached reading — upstream source is temporarily unreachable"
            }
            className="inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-amber-400/15 px-1.5 py-0.5 text-[9px] font-semibold tracking-[0.14em] text-amber-700"
          >
            <AlertTriangle size={9} aria-hidden /> STALE
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function formatRelativeAge(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return new Date(ts).toLocaleString();
}

// Weather data updates once an hour, on the :45 minute mark.
const ONE_HOUR = 60 * 60_000;
const hourlyAt45 = () => msUntilMinuteMark(45);

function WeatherPage() {
  const weather = useQuery({
    queryKey: ["weather", "coffman-cove"],
    queryFn: ({ signal }) => fetchMarineWeather(signal),
    staleTime: ONE_HOUR,
    gcTime: 12 * ONE_HOUR,
    refetchInterval: hourlyAt45,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(30_000, 1000 * 2 ** attempt),
  });

  const marine = useQuery({
    queryKey: ["marine", "coffman-cove"],
    queryFn: ({ signal }) => fetchMarineConditions(signal),
    staleTime: ONE_HOUR,
    gcTime: 12 * ONE_HOUR,
    refetchInterval: hourlyAt45,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(30_000, 1000 * 2 ** attempt),
  });

  const tides = useQuery({
    queryKey: ["tides", "coffman-cove"],
    queryFn: ({ signal }) => fetchTides(signal),
    staleTime: ONE_HOUR,
    refetchInterval: hourlyAt45,
    refetchOnWindowFocus: false,
  });

  const tideHeights = useQuery({
    queryKey: ["tide-heights", "coffman-cove"],
    queryFn: ({ signal }) => fetchTideHeights(signal),
    staleTime: ONE_HOUR,
    refetchInterval: hourlyAt45,
    refetchOnWindowFocus: false,
  });


  const nowMs = Date.now();
  const todayKeyAK = new Date(nowMs).toLocaleDateString("en-CA", {
    timeZone: "America/Anchorage",
  });
  const todaysTides: TidePrediction[] = (tides.data ?? []).filter(
    (t) =>
      new Date(t.t).toLocaleDateString("en-CA", {
        timeZone: "America/Anchorage",
      }) === todayKeyAK,
  );


  const current = weather.data?.current;
  const daily = weather.data?.daily;
  const hourly = weather.data?.hourly;
  const marineCurrent = marine.data?.current;

  // Fallback state: we have prior data but the latest refetch failed.
  const showingStaleFallback = weather.isError && !!weather.data;
  // Hard failure: no data at all AND the request errored.
  const showingHardError = weather.isError && !weather.data;

  // Per-widget stale flags — mark each source independently so users can tell
  // which readings are live vs served from cache after an upstream failure.
  const weatherStale = weather.isError && !!weather.data;
  const marineStale = marine.isError && !!marine.data;
  const weatherStaleAge = weather.dataUpdatedAt ? formatRelativeAge(weather.dataUpdatedAt) : undefined;
  const marineStaleAge = marine.dataUpdatedAt ? formatRelativeAge(marine.dataUpdatedAt) : undefined;

  // Compute a naive visibility approximation from weather code / precipitation
  // (Open-Meteo free tier does not always expose visibility in current).
  const visibility = (() => {
    const code = current?.weather_code;
    if (code === undefined) return { miles: null as number | null, label: "—" };
    if ([45, 48].includes(code)) return { miles: 0.5, label: "Fog" };
    if ([61, 63, 65, 80, 81, 82, 95].includes(code)) return { miles: 3, label: "Reduced" };
    if ([51, 53, 55, 71, 73, 75].includes(code)) return { miles: 5, label: "Fair" };
    if ([2, 3].includes(code)) return { miles: 8, label: "Good" };
    return { miles: 10, label: "Excellent" };
  })();

  return (
    <main className="min-h-screen bg-[var(--color-parchment)]">
      {/* Hero */}
      <section className="border-b border-border bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12 md:py-16">
          <p className="text-xs uppercase tracking-[0.28em] text-[var(--color-ember)] mb-3">
            Fishing · Weather
          </p>
          <h1 className="text-4xl md:text-5xl font-serif tracking-tight">
            Coffman Cove weather tracker
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Live conditions and 7-day at-a-glance forecast for the waters around Coffman
            Cove, Prince of Wales Island, Alaska. Data refreshes twice a day.

          </p>
          <p className="mt-2 text-sm text-white/60">
            {COFFMAN_COVE.lat.toFixed(3)}°N, {Math.abs(COFFMAN_COVE.lon).toFixed(3)}°W
          </p>
        </div>
      </section>

      {/* Loading — only while there's no data to show yet */}
      {weather.isLoading && !weather.data && (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 flex items-center gap-3 text-muted-foreground">
          <Loader2 className="animate-spin" size={18} /> Loading live weather…
        </div>
      )}

      {/* Hard error: no cached data available at all */}
      {showingHardError && (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
          <div className="rounded-xl border border-red-300 bg-red-50 p-6 text-red-900">
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle size={18} />
              Weather service unavailable
            </div>
            <p className="mt-2 text-sm">
              We couldn't reach the weather service and don't have a recent
              reading cached. Please try again in a few minutes.
            </p>
            <button
              onClick={() => {
                weather.refetch();
                marine.refetch();
              }}
              className="mt-4 inline-flex items-center rounded-md bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800"
            >
              Retry now
            </button>
          </div>
        </div>
      )}

      {/* Soft fallback: latest refetch failed but we still have prior data */}
      {showingStaleFallback && (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-6">
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-semibold">Showing last known conditions</div>
              <p className="mt-1">
                The weather service is temporarily unreachable. The readings
                below are from the most recent successful update and may be
                out of date.
              </p>
            </div>
            <button
              onClick={() => {
                weather.refetch();
                marine.refetch();
              }}
              className="rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              Retry
            </button>
          </div>
        </div>
      )}



      {weather.data && (
        <>
          {/* Current widgets */}
          <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
            <h2 className="text-2xl font-serif mb-6">Current conditions</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Current weather */}
              <Widget title="Weather" icon={codeInfo(current?.weather_code).icon} stale={weatherStale} staleAgeLabel={weatherStaleAge}>
                <div className="text-4xl font-serif">
                  {Math.round(current?.temperature_2m ?? 0)}°F
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {codeInfo(current?.weather_code).label}
                </div>
                <div className="mt-4 flex items-center gap-4 text-sm">
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Thermometer size={14} />
                    Feels {Math.round(current?.apparent_temperature ?? 0)}°
                  </span>
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Droplets size={14} />
                    {Math.round(current?.relative_humidity_2m ?? 0)}%
                  </span>
                </div>
              </Widget>

              {/* Precipitation */}
              <Widget title="Precipitation" icon={CloudRain} stale={weatherStale} staleAgeLabel={weatherStaleAge}>
                <div className="text-4xl font-serif">
                  {(current?.precipitation ?? 0).toFixed(2)}"
                </div>
                <div className="text-sm text-muted-foreground mt-1">Last hour</div>
                <div className="mt-4 text-sm text-muted-foreground">
                  Today's chance:{" "}
                  <span className="text-foreground font-medium">
                    {daily?.precipitation_probability_max?.[0] ?? 0}%
                  </span>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Total today:{" "}
                  <span className="text-foreground font-medium">
                    {(daily?.precipitation_sum?.[0] ?? 0).toFixed(2)}"
                  </span>
                </div>
              </Widget>

              {/* Wind */}
              <Widget title="Wind" icon={Wind} stale={weatherStale} staleAgeLabel={weatherStaleAge}>
                <div className="text-4xl font-serif">
                  {Math.round(current?.wind_speed_10m ?? 0)}
                  <span className="text-lg text-muted-foreground ml-1">kn</span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  From {degToCompass(current?.wind_direction_10m ?? 0)}{" "}
                  ({Math.round(current?.wind_direction_10m ?? 0)}°)
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <Compass size={14} />
                  Gusts to{" "}
                  <span className="text-foreground font-medium">
                    {Math.round(current?.wind_gusts_10m ?? 0)} kn
                  </span>
                </div>
              </Widget>

              {/* Visibility */}
              <Widget title="Visibility" icon={Eye} stale={weatherStale} staleAgeLabel={weatherStaleAge}>
                <div className="text-4xl font-serif">
                  {visibility.miles !== null ? `${visibility.miles}` : "—"}
                  <span className="text-lg text-muted-foreground ml-1">mi</span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {visibility.label}
                </div>
                <div className="mt-4 text-sm text-muted-foreground">
                  Pressure:{" "}
                  <span className="text-foreground font-medium inline-flex items-center gap-1">
                    <Gauge size={14} />
                    {Math.round(current?.pressure_msl ?? 0)} hPa
                  </span>
                </div>
              </Widget>
            </div>

            {/* Marine strip */}
            {marineCurrent && (
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <Widget title="Wave Height" icon={Waves} stale={marineStale} staleAgeLabel={marineStaleAge}>
                  <div className="text-2xl font-serif">
                    {(marineCurrent.wave_height ?? 0).toFixed(1)} ft
                  </div>
                </Widget>
                <Widget title="Wave Period" icon={Waves} stale={marineStale} staleAgeLabel={marineStaleAge}>
                  <div className="text-2xl font-serif">
                    {(marineCurrent.wave_period ?? 0).toFixed(1)} s
                  </div>
                </Widget>
                <Widget title="Sea Surface Temp" icon={Thermometer} stale={marineStale} staleAgeLabel={marineStaleAge}>
                  <div className="text-2xl font-serif">
                    {marineCurrent.sea_surface_temperature !== undefined
                      ? `${Math.round(marineCurrent.sea_surface_temperature)}°F`
                      : "—"}
                  </div>
                </Widget>
              </div>
            )}
          </section>

          {/* Today's tides */}
          <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16">
            <h2 className="text-2xl font-serif mb-6">Today's tides</h2>
            <div className="rounded-xl border border-border bg-background/60 backdrop-blur p-4 sm:p-6">
              {tideHeights.isLoading || tides.isLoading ? (
                <p className="text-sm text-muted-foreground">
                  Loading tide predictions…
                </p>
              ) : todaysTides.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Tide predictions unavailable right now.
                </p>
              ) : (
                <>
                  <TideDayChart
                    heights={tideHeights.data ?? []}
                    now={nowMs}
                    className="w-full text-foreground"
                  />
                  <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {todaysTides.map((t) => {
                      const high = t.type === "H";
                      return (
                        <div
                          key={t.t}
                          className="rounded-lg border border-border bg-background/70 px-4 py-3 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <Waves
                              size={16}
                              className={
                                high
                                  ? "text-[var(--color-ember)]"
                                  : "text-muted-foreground"
                              }
                              aria-hidden
                            />
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                              {high ? "High" : "Low"}
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="text-base font-semibold">
                              {new Date(t.t).toLocaleTimeString("en-US", {
                                timeZone: "America/Anchorage",
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {Number(t.v).toFixed(1)} ft
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* 7-day forecast */}

          {daily && (
            <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16 text-center">
              <h2 className="text-2xl font-serif mb-6">7-day forecast</h2>
              <div className="mx-auto grid justify-center gap-3 grid-cols-2 sm:grid-cols-4 lg:grid-cols-7">
                {(() => {
                  const todayKey = new Date().toLocaleDateString("en-CA", {
                    timeZone: "America/Anchorage",
                  });
                  const times: string[] = daily.time ?? [];
                  const start = Math.max(
                    0,
                    times.findIndex((t: string) => t.slice(0, 10) >= todayKey),
                  );
                  return times.slice(start, start + 7).map((iso: string, idx: number) => {
                  const i = start + idx;
                  const info = codeInfo(daily.weather_code?.[i]);
                  const Icon = info.icon;
                  const date = new Date(iso);
                  const day = date.toLocaleDateString("en-US", {
                    weekday: "short",
                  });
                  const dayNum = date.getDate();
                  return (
                    <div
                      key={iso}
                      className="rounded-xl border border-border bg-background/60 backdrop-blur p-4 text-center"
                    >
                      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        {day} {dayNum}
                      </div>
                      <Icon
                        size={28}
                        className="mx-auto my-3 text-[var(--color-ember)]"
                      />
                      <div className="text-sm font-medium">{info.label}</div>
                      <div className="mt-2 text-sm">
                        <span className="font-semibold">
                          {Math.round(daily.temperature_2m_max?.[i] ?? 0)}°
                        </span>{" "}
                        <span className="text-muted-foreground">
                          / {Math.round(daily.temperature_2m_min?.[i] ?? 0)}°
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <CloudRain size={12} />
                        {daily.precipitation_probability_max?.[i] ?? 0}%
                      </div>
                      <div className="mt-1 flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <Wind size={12} />
                        {Math.round(daily.wind_speed_10m_max?.[i] ?? 0)} kn
                      </div>
                    </div>
                  );
                });
                })()}
              </div>
            </section>
          )}

          {/* Sun times */}
          {daily?.sunrise?.[0] && daily?.sunset?.[0] && (
            <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-20">
              <div className="grid gap-4 sm:grid-cols-2">
                <Widget title="Sunrise today" icon={Sunrise}>
                  <div className="text-2xl font-serif">
                    {new Date(daily.sunrise[0]).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </Widget>
                <Widget title="Sunset today" icon={Sunset}>
                  <div className="text-2xl font-serif">
                    {new Date(daily.sunset[0]).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </Widget>
              </div>
            </section>
          )}

          <p className="mx-auto max-w-6xl px-4 sm:px-6 pb-16 text-xs text-muted-foreground">
            Data from Open-Meteo. Hourly samples: {hourly?.time?.length ?? 0}.
          </p>
        </>
      )}
    </main>
  );
}
